import type { Currency } from '@ps/web-core-enums';
import { zonedTimeToUtc } from 'date-fns-tz';
import { get, isUndefined } from 'lodash';

import type {
  AccumulatedLossLimitType,
  BaseGameLimits,
  DepositLimitValues,
  LimitCriteria,
  LimitFormValue,
  SpendLimitType
} from './definitions';

export const LIMIT_UNSET_VALUE = -1;
export const BLOCKED_LIMIT_VALUE = 0;

export type LimitTypes = { limitType: string; type: number }[];

export const convertLimitsToDecimalValues = (gameLimits: BaseGameLimits, limitTypes: LimitTypes) =>
  Object.keys(gameLimits).map((item) => {
    const amount = gameLimits[item];
    return {
      type: limitTypes.find(({ limitType }) => limitType === item)?.type,
      amount: amount < 0 ? amount : amount * 100
    };
  });

export const convertLimitsToIntValues = (limits: LimitCriteria[], limitTypes: LimitTypes) =>
  limits.reduce(
    (memo, { type, amount }) => {
      const limitName = limitTypes.find((limitType) => limitType.type === type)?.limitType;
      if (limitName) {
        memo[limitName] = amount < 0 ? amount : amount / 100;
      }
      return memo;
    },
    {} as Record<string, number>
  );

export const getMinutes = (limit: LimitFormValue | undefined): number => {
  if (!limit) {
    return 0;
  }

  const days = Number(limit.days) || 0;
  const hours = Number(limit.hours) || 0;
  const minutes = Number(limit.minutes) || 0;

  return days * 24 * 60 + hours * 60 + minutes;
};

export const hasNoLimitsChange = ({
  dailyLimit,
  weeklyLimit,
  monthlyLimit,
  previousDailyLimit,
  previousWeeklyLimit,
  previousMonthlyLimit
}: DepositLimitValues): boolean =>
  (isUndefined(dailyLimit) || dailyLimit * 100 === previousDailyLimit) &&
  (isUndefined(weeklyLimit) || weeklyLimit * 100 === previousWeeklyLimit) &&
  (isUndefined(monthlyLimit) || monthlyLimit * 100 === previousMonthlyLimit);

export const getLimitAmountByType = (
  limits: LimitCriteria[],
  limitType: SpendLimitType | AccumulatedLossLimitType
): number =>
  get(
    limits.find(({ type }) => type === limitType),
    'amount',
    LIMIT_UNSET_VALUE
  );

export const getChangedLimit = (
  type: SpendLimitType | AccumulatedLossLimitType,
  currency: Currency,
  previousLimit?: number,
  currentLimit?: number,
  removeLimit = false
): LimitCriteria | undefined => {
  if (removeLimit) {
    return { amount: LIMIT_UNSET_VALUE, currency, type };
  }
  const amount = Number(currentLimit) * 100;
  if (!isUndefined(currentLimit) && previousLimit !== amount) {
    return { amount, currency, type };
  }

  return;
};

export const checkIncrease = <T>(
  values: T,
  initialValues: Partial<T>,
  limitTypes: Omit<LimitTypes, 'type'>
) =>
  limitTypes.reduce((hasIncrease: boolean, { limitType }) => {
    const initiallValue = Number(initialValues[limitType as keyof typeof initialValues]);
    const value = Number(values[limitType as keyof typeof values]);
    if (initiallValue > 0 && value > 0 && value > initiallValue) {
      hasIncrease = true;
    }

    return hasIncrease;
  }, false);

export const toTimestamp = (date: number[]) => {
  const [year, month, day, hours, minutes, seconds = 0] = date;

  return zonedTimeToUtc(new Date(year, month - 1, day, hours, minutes, seconds), 'America/Toronto');
};
